import { useState, useEffect } from "react";
import { CheckCircle, Wrench, Loader2, AlertCircle, PartyPopper } from "lucide-react";
import { Button } from "../components/ui/button";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WorkComplete() {
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [error, setError] = useState(null);
  const [orderNumber, setOrderNumber] = useState("");

  // Get token from URL
  const token = window.location.pathname.split("/").pop();

  const handleComplete = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await authFetch(`${API}/public/work-order/${token}/complete`);
      const data = await res.json();

      if (res.ok) {
        if (data.already_completed) {
          setAlreadyCompleted(true);
        } else {
          setCompleted(true);
        }
        setOrderNumber(data.work_order_number || "");
      } else {
        setError(data.detail || "Errore nella conferma");
      }
    } catch (err) {
      setError("Errore di connessione. Riprova più tardi.");
    } finally {
      setLoading(false);
    }
  };

  // Show confirmation screen
  if (completed) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-900 to-zinc-900 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-lg p-8 max-w-md w-full text-center shadow-2xl border border-green-500/30">
          <div className="mb-6">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <PartyPopper className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Lavoro Completato!
            </h1>
            {orderNumber && (
              <p className="text-zinc-400">
                Ordine #{orderNumber}
              </p>
            )}
          </div>
          
          <div className="bg-green-500/10 border border-green-500/30 rounded p-4 mb-6">
            <CheckCircle className="w-8 h-8 text-green-400 mx-auto mb-2" />
            <p className="text-green-300 text-sm">
              La conferma è stata registrata con successo.
              L'officina è stata notificata del completamento.
            </p>
          </div>

          <p className="text-zinc-500 text-sm">
            Puoi chiudere questa pagina.
          </p>
        </div>
      </div>
    );
  }

  // Show already completed screen
  if (alreadyCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 to-zinc-900 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-lg p-8 max-w-md w-full text-center shadow-2xl border border-blue-500/30">
          <div className="mb-6">
            <div className="w-20 h-20 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-blue-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Già Completato
            </h1>
            {orderNumber && (
              <p className="text-zinc-400">
                Ordine #{orderNumber}
              </p>
            )}
          </div>
          
          <div className="bg-blue-500/10 border border-blue-500/30 rounded p-4 mb-6">
            <p className="text-blue-300 text-sm">
              Questo lavoro è già stato segnato come completato.
              Non è necessaria alcuna altra azione.
            </p>
          </div>

          <p className="text-zinc-500 text-sm">
            Puoi chiudere questa pagina.
          </p>
        </div>
      </div>
    );
  }

  // Show error screen
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-900 to-zinc-900 flex items-center justify-center p-4">
        <div className="bg-zinc-900 rounded-lg p-8 max-w-md w-full text-center shadow-2xl border border-red-500/30">
          <div className="mb-6">
            <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Errore
            </h1>
          </div>
          
          <div className="bg-red-500/10 border border-red-500/30 rounded p-4 mb-6">
            <p className="text-red-300 text-sm">
              {error}
            </p>
          </div>

          <Button 
            onClick={() => setError(null)} 
            variant="outline"
            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
          >
            Riprova
          </Button>
        </div>
      </div>
    );
  }

  // Main confirmation screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/20 to-zinc-900 flex items-center justify-center p-4">
      <div className="bg-zinc-900 rounded-lg p-8 max-w-md w-full text-center shadow-2xl border border-primary/30">
        <div className="mb-6">
          <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Wrench className="w-10 h-10 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            Conferma Fine Lavoro
          </h1>
          <p className="text-zinc-400">
            Clicca il pulsante per confermare che il lavoro è stato completato.
          </p>
        </div>
        
        <div className="bg-zinc-800/50 rounded p-4 mb-6">
          <p className="text-zinc-300 text-sm">
            Questa azione aggiornerà lo stato dell'ordine di lavoro e libererà 
            lo slot nell'agenda dell'officina.
          </p>
        </div>

        <Button 
          onClick={handleComplete} 
          disabled={loading}
          className="w-full btn-primary py-6 text-lg"
          data-testid="confirm-complete-btn"
        >
          {loading ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Conferma in corso...
            </>
          ) : (
            <>
              <CheckCircle className="w-5 h-5 mr-2" />
              Conferma Completamento
            </>
          )}
        </Button>

        <p className="text-zinc-600 text-xs mt-4">
          Link monouso - funziona una sola volta
        </p>
      </div>
    </div>
  );
}
